import type { FC, PropsWithChildren } from "react";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@babybjorn/ui";
import Icon from "../Icon";
import type { Material } from "../../../utils/materials";
import { compact } from "lodash";
import MaterialListItem from "../Modal/components/MaterialListItem";
import cn from "classnames";

type VariantPickerProps = PropsWithChildren<{
	colorLabel?: string;
	fabricLabel?: string;
	toyLabel?: string;
	chosenColor?: string;
	chosenFabric?: string;
	chosenToy?: string;
	materialLabel?: string;
	materials?: Material[];
}>;

const VariantPicker: FC<VariantPickerProps> = ({
	children,
	colorLabel,
	fabricLabel,
	toyLabel,
	chosenColor,
	chosenFabric,
	chosenToy,
	materialLabel,
	materials,
}) => {
	const fabricItems = compact(
		materials?.map(({ material }) =>
			(material?.description || material?.bullets) &&
			material.name &&
			material.image?.filename
				? {
						fabric: material.name,
						image: {
							src: material.image.filename,
							alt: material.image.alt ?? undefined,
						},
						description: material.description,
						bullets: material.bullets,
					}
				: undefined,
		),
	);

	return (
		<form className="space-y-4">
			<fieldset className="flex w-full flex-wrap gap-2">{children}</fieldset>

			<div>
				{chosenColor && (
					<div className="text-gray-4 flex items-center gap-x-1">
						<p className="text-gray-2 !bb-text-tiny">{colorLabel}:</p>
						<p className="!bb-text-tiny">{chosenColor}</p>
					</div>
				)}
				{chosenFabric && (
					<div className="text-gray-4 flex items-center gap-x-1">
						<p className="text-gray-2 !bb-text-tiny">{fabricLabel}:</p>
						<Sheet>
							<SheetTrigger
								aria-label={materialLabel}
								className={cn(
									"!active:border-0 tab-focus flex !h-fit gap-0 !border-0 !p-0",
									fabricItems.length === 0 && "pointer-events-none",
								)}
							>
								<p className="!bb-text-tiny">{chosenFabric}</p>
								{fabricItems.length > 0 && (
									<Icon icon="arrow-right" width={20} />
								)}
								<span className="sr-only">
									Additional information about {materialLabel}
								</span>
							</SheetTrigger>
							<SheetContent>
								<SheetHeader className="mb-4">
									<SheetTitle>{materialLabel}</SheetTitle>
								</SheetHeader>
								<div className="-mr-6 flex h-full flex-col gap-y-4 overflow-y-auto pb-30">
									<MaterialListItem fabrics={fabricItems} />
								</div>
							</SheetContent>
						</Sheet>
					</div>
				)}
				{chosenToy && (
					<div className="text-gray-4 flex items-center gap-x-1">
						<p className="text-gray-2 !bb-text-tiny">{toyLabel}:</p>
						<p className="!bb-text-tiny">{chosenToy}</p>
					</div>
				)}
			</div>
		</form>
	);
};

export default VariantPicker;
